import { Package, Plus, Edit2, Trash2, Loader2, DollarSign, Calendar } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';

import { supabase } from '../../../lib/supabase';
import { SubscriptionForm } from './SubscriptionForm';

interface Subscription {
  id: string;
  name: string;
  price: number;
  subscription_type: string;
  access_type: string;
  access_value: number;
  contract_term: string;
  billing_cycle: string;
  prepayment_required: boolean;
  prepayment_months: number;
  is_free_trial: boolean;
  requires_approval: boolean;
  allows_cash_payment: boolean;
  trial_duration_days: number | null;
  trial_duration_classes: number | null;
  trial_duration_period: string | null;
  created_at: string;
  sports_included: string[];
  amenities_included: string[];
}

interface SubscriptionWizardProps {
  companyId: string;
  onComplete: () => void;
  onBack: () => void;
  showCompleteButton?: boolean;
}

type SubscriptionView = 'list' | 'form';

export function SubscriptionWizard({ companyId, onComplete, onBack, showCompleteButton = true }: SubscriptionWizardProps) {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<SubscriptionView>('list');
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);

  const loadSubscriptions = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data: subs, error } = await supabase
        .from('company_subscriptions')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubscriptions(subs || []);
    } catch {
      // Error loading subscriptions
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      loadSubscriptions();
    }
  }, [companyId, loadSubscriptions]);

  const handleCreate = () => {
    setEditingSubscription(null);
    setCurrentView('form');
  };

  const handleEdit = (subscription: Subscription) => {
    setEditingSubscription(subscription);
    setCurrentView('form');
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) return;

    try {
      const { error } = await supabase
        .from('company_subscriptions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSubscriptions(prev => prev.filter(s => s.id !== id));
    } catch {
      alert('Failed to delete subscription');
    }
  };

  const handleSave = (subscription: Subscription) => {
    if (editingSubscription) {
      setSubscriptions(prev => prev.map(s => s.id === subscription.id ? subscription : s));
    } else {
      setSubscriptions(prev => [subscription, ...prev]);
    }
    setCurrentView('list');
    setEditingSubscription(null);
  };

  const handleCancel = () => {
    setCurrentView('list');
    setEditingSubscription(null);
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'recurring': return 'Recurring';
      case 'punch_card': return 'Punch Card';
      case 'trial': return 'Trial';
      case 'drop_in': return 'Drop-In';
      default: return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'recurring': return 'bg-green-600/20 text-green-500';
      case 'punch_card': return 'bg-blue-600/20 text-blue-500';
      case 'trial': return 'bg-yellow-600/20 text-yellow-500';
      case 'drop_in': return 'bg-purple-600/20 text-purple-500';
      default: return 'bg-gray-600/20 text-gray-500';
    }
  };

  const getAccessLabel = (sub: Subscription) => {
    if (sub.access_type === 'unlimited') return 'Unlimited';
    if (sub.access_type === 'days') return `${sub.access_value} days/month`;
    if (sub.access_type === 'classes') return `${sub.access_value} classes/month`;
    return sub.access_type;
  };

  if (loading) {
    return (
      <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-rollops-accent" />
        </div>
      </div>
    );
  }

  if (currentView === 'form') {
    return (
      <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
        <SubscriptionForm
          key={editingSubscription?.id || 'new'}
          companyId={companyId}
          subscription={editingSubscription}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
          <Package size={32} className="text-text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Subscriptions & Discounts</h2>
        <p className="text-[#A9A3A3] mt-2">Create your membership plans</p>
      </div>

      {subscriptions.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-20 h-20 rounded-full bg-[#363232] flex items-center justify-center mx-auto mb-4">
            <Package size={40} className="text-[#504A4A]" />
          </div>
          <h3 className="text-xl font-semibold text-[#EBFFEE] mb-2">No Subscriptions Yet</h3>
          <p className="text-[#A9A3A3] mb-6">Create your first subscription plan for members</p>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-medium rounded-lg transition-colors"
          >
            <Plus size={18} />
            Create First Subscription
          </button>
        </div>
      ) : (
        <>
          <div className="mb-4 flex justify-end">
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-medium rounded-lg transition-colors"
            >
              <Plus size={18} />
              Add Subscription
            </button>
          </div>

          <div className="space-y-3 mb-6 max-h-[400px] overflow-y-auto">
            {subscriptions.map((sub) => (
              <div
                key={sub.id}
                className="bg-[#363232] rounded-xl border border-[#504A4A] p-4 hover:border-[#9B1421]/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#EBFFEE] text-lg">{sub.name}</h3>
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full mt-1 ${getTypeColor(sub.subscription_type)}`}>
                      {getTypeLabel(sub.subscription_type)}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEdit(sub)}
                      className="p-2 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#504A4A] rounded-lg transition-colors"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(sub.id)}
                      className="p-2 text-[#A9A3A3] hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <DollarSign size={14} className="text-rollops-accent" />
                    <span className="text-[#EBFFEE] font-medium">
                      ${sub.price.toFixed(2)}
                      {sub.subscription_type === 'recurring' && (
                        <span className="text-[#A9A3A3] font-normal text-xs ml-1">
                          /{sub.billing_cycle === 'monthly' ? 'mo' : sub.billing_cycle}
                        </span>
                      )}
                    </span>
                    {sub.is_free_trial && (
                      <span className="text-xs bg-green-600/20 text-green-500 px-2 py-0.5 rounded-full">Free</span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-[#A9A3A3]" />
                    <span className="text-[#A9A3A3]">{getAccessLabel(sub)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="flex gap-4 pt-4 border-t border-[#504A4A]">
        <button
          onClick={onBack}
          className="flex-1 py-3 px-4 border border-[#504A4A] hover:bg-[#363232] text-[#A9A3A3] font-semibold rounded-lg transition-colors"
        >
          Back
        </button>
        {showCompleteButton && (
          <button
            onClick={onComplete}
            className="flex-1 py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
          >
            Complete Setup
          </button>
        )}
        {!showCompleteButton && (
          <button
            onClick={onComplete}
            className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white font-semibold rounded-lg transition-colors"
          >
            Done
          </button>
        )}
      </div>
    </div>
  );
}
